import git from 'isomorphic-git';
import {
  dropAnnotationsForPath,
  remapAnnotationsForMove,
  withAnnotation,
  type EntryAnnotation,
} from './annotations';
import { repositoryFileBytes } from './bytes';
import { annotationsFromFiles, isHiddenMetaPath, TRV_META_PATH, withAnnotationsFile } from './trv';
import type { RepositoryCommit, RepositoryFile, RepositorySnapshot } from './types';

export * from './agentsGuide';
export * from './annotations';
export * from './bytes';
export * from './manifest';
export * from './trv';
export type * from './types';

const encoder = new TextEncoder();

/** Annotations live in the tracked root `.trv` blob (not a sidecar snapshot field). */
export const annotationsOf = (snapshot: Pick<RepositorySnapshot, 'files'>) =>
  annotationsFromFiles(snapshot.files);

const assertMutableUserPath = (path: string, action: string) => {
  if (isHiddenMetaPath(path)) {
    throw new Error(`Cannot ${action} internal meta path ${TRV_META_PATH}`);
  }
};

export interface RepositoryStore {
  load(repositoryId: string): Promise<RepositorySnapshot | null>;
  save(snapshot: RepositorySnapshot): Promise<void>;
  clear(repositoryId: string): Promise<void>;
}

const canonicalFiles = (files: RepositoryFile[]) =>
  [...files].sort((a, b) => a.path.localeCompare(b.path));

const requireContent = (file: RepositoryFile): string => {
  if (file.content === undefined) throw new Error(`File content is not loaded: ${file.path}`);
  return file.content;
};

const sha1 = async (value: Uint8Array): Promise<string> => {
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-1', value as BufferSource));
  return [...digest].map((byte) => byte.toString(16).padStart(2, '0')).join('');
};

const gitHash = async (type: 'tree' | 'commit', body: Uint8Array): Promise<string> => {
  const header = encoder.encode(`${type} ${body.length}\0`);
  const bytes = new Uint8Array(header.length + body.length);
  bytes.set(header);
  bytes.set(body, header.length);
  return sha1(bytes);
};

const hexBytes = (value: string) =>
  Uint8Array.from(value.match(/.{2}/g) ?? [], (byte) => Number.parseInt(byte, 16));

const concat = (...values: Uint8Array[]) => {
  const result = new Uint8Array(values.reduce((length, value) => length + value.length, 0));
  let offset = 0;
  for (const value of values) {
    result.set(value, offset);
    offset += value.length;
  }
  return result;
};

const compareGitNames = (
  left: { name: string; mode: string },
  right: { name: string; mode: string },
) => {
  const leftBytes = encoder.encode(`${left.name}${left.mode === '40000' ? '/' : ''}`);
  const rightBytes = encoder.encode(`${right.name}${right.mode === '40000' ? '/' : ''}`);
  const length = Math.min(leftBytes.length, rightBytes.length);
  for (let index = 0; index < length; index += 1) {
    if (leftBytes[index] !== rightBytes[index]) return leftBytes[index]! - rightBytes[index]!;
  }
  return leftBytes.length - rightBytes.length;
};

interface TreeNode {
  blobs: Map<string, string>;
  trees: Map<string, TreeNode>;
}

const treeNode = (): TreeNode => ({ blobs: new Map(), trees: new Map() });

const gitTree = async (files: Array<{ path: string; blob: string }>): Promise<string> => {
  const root = treeNode();
  for (const file of files) {
    const parts = file.path.split('/').filter(Boolean);
    const name = parts.pop();
    if (!name) throw new Error('Files must have a non-empty path');
    let node = root;
    for (const part of parts) {
      const child = node.trees.get(part) ?? treeNode();
      node.trees.set(part, child);
      node = child;
    }
    node.blobs.set(name, file.blob);
  }

  const encodeNode = async (node: TreeNode): Promise<string> => {
    const entries: Array<{ name: string; mode: string; id: string }> = [];
    for (const [name, child] of node.trees) {
      entries.push({ name, mode: '40000', id: await encodeNode(child) });
    }
    for (const [name, id] of node.blobs) entries.push({ name, mode: '100644', id });
    entries.sort(compareGitNames);
    const body = concat(
      ...entries.map((entry) =>
        concat(encoder.encode(`${entry.mode} ${entry.name}\0`), hexBytes(entry.id)),
      ),
    );
    return gitHash('tree', body);
  };

  return encodeNode(root);
};

export const createCommit = async (
  files: RepositoryFile[],
  previous: RepositoryCommit | null,
  message: string,
  author: string,
  createdAt = new Date().toISOString(),
  authorEmail = 'local@trove',
  committer = author,
  committerEmail = authorEmail,
): Promise<RepositoryCommit> => {
  const normalized = canonicalFiles(files);
  for (const file of normalized) requireContent(file);
  const entries = await Promise.all(
    normalized.map(async (file) => {
      const blob = await git.hashBlob({ object: repositoryFileBytes(file) });
      return { path: file.path, blob: blob.oid, mode: '100644' };
    }),
  );
  const treeId = await gitTree(entries);
  const timestamp = Math.floor(new Date(createdAt).getTime() / 1000);
  const commitBody = encoder.encode(
    `tree ${treeId}\n${previous ? `parent ${previous.id}\n` : ''}author ${author} <${authorEmail}> ${timestamp} +0000\ncommitter ${committer} <${committerEmail}> ${timestamp} +0000\n\n${message.trimEnd()}\n`,
  );
  return {
    id: await gitHash('commit', commitBody),
    treeId,
    parentId: previous?.id ?? null,
    message,
    author,
    authorEmail,
    committer,
    committerEmail,
    createdAt,
    authorTimestamp: timestamp,
    authorTimezone: '+0000',
    committerTimestamp: timestamp,
    committerTimezone: '+0000',
    files: normalized,
  };
};

export const updateFile = async (
  snapshot: RepositorySnapshot,
  path: string,
  content: string,
  mimeType: string,
  message: string,
  author: string,
): Promise<RepositorySnapshot> => {
  assertMutableUserPath(path, 'write');
  const now = new Date().toISOString();
  const nextFile: RepositoryFile = {
    path,
    content,
    mimeType,
    size: encoder.encode(content).length,
    modifiedAt: now,
  };
  const files = [...snapshot.files.filter((file) => file.path !== path), nextFile];
  const commit = await createCommit(files, snapshot.commits.at(-1) ?? null, message, author, now);
  return {
    ...snapshot,
    files: canonicalFiles(files),
    commits: [...snapshot.commits, commit],
    head: commit.id,
    version: snapshot.version + 1,
  };
};

export const updateFiles = async (
  snapshot: RepositorySnapshot,
  updates: Array<{ path: string; content: string; mimeType: string }>,
  message: string,
  author: string,
): Promise<RepositorySnapshot> => {
  if (!updates.length) return snapshot;
  for (const update of updates) assertMutableUserPath(update.path, 'write');
  const now = new Date().toISOString();
  const paths = new Set(updates.map((update) => update.path));
  if (paths.size !== updates.length) throw new Error('Dropped files must have unique names');
  const nextFiles: RepositoryFile[] = updates.map((update) => ({
    path: update.path,
    content: update.content,
    mimeType: update.mimeType,
    size: encoder.encode(update.content).length,
    modifiedAt: now,
  }));
  const files = [...snapshot.files.filter((file) => !paths.has(file.path)), ...nextFiles];
  const commit = await createCommit(files, snapshot.commits.at(-1) ?? null, message, author, now);
  return {
    ...snapshot,
    files: canonicalFiles(files),
    commits: [...snapshot.commits, commit],
    head: commit.id,
    version: snapshot.version + 1,
  };
};

export const removeFile = async (
  snapshot: RepositorySnapshot,
  path: string,
  author: string,
): Promise<RepositorySnapshot> => {
  const source = path.replace(/^\/+|\/+$/g, '');
  if (!source) throw new Error('Choose a file or folder to delete');
  assertMutableUserPath(source, 'delete');
  const remaining = snapshot.files.filter(
    (file) => file.path !== source && !file.path.startsWith(`${source}/`),
  );
  if (remaining.length === snapshot.files.length)
    throw new Error('The source path no longer exists');
  const files = withAnnotationsFile(
    remaining,
    dropAnnotationsForPath(annotationsOf(snapshot), source),
  );
  const commit = await createCommit(
    files,
    snapshot.commits.at(-1) ?? null,
    `Delete ${source}`,
    author,
  );
  return {
    ...snapshot,
    files: canonicalFiles(files),
    commits: [...snapshot.commits, commit],
    head: commit.id,
    version: snapshot.version + 1,
  };
};

export const movePath = async (
  snapshot: RepositorySnapshot,
  sourcePath: string,
  targetFolder: string,
  author: string,
): Promise<RepositorySnapshot> => {
  const source = sourcePath.replace(/^\/+|\/+$/g, '');
  const target = targetFolder.replace(/^\/+|\/+$/g, '');
  if (!source) throw new Error('Choose a file or folder to move');
  assertMutableUserPath(source, 'move');
  if (target === source || target.startsWith(`${source}/`)) {
    throw new Error('A folder cannot be moved into itself');
  }

  const affected = snapshot.files.filter(
    (file) =>
      !isHiddenMetaPath(file.path) && (file.path === source || file.path.startsWith(`${source}/`)),
  );
  if (!affected.length) throw new Error('The source path no longer exists');

  const name = source.split('/').at(-1)!;
  const destination = target ? `${target}/${name}` : name;
  if (destination === source) throw new Error('This item is already in that folder');

  const affectedPaths = new Set(affected.map((file) => file.path));
  const moves = new Map(
    affected.map((file) => [file.path, `${destination}${file.path.slice(source.length)}`]),
  );
  for (const nextPath of moves.values()) {
    if (snapshot.files.some((file) => !affectedPaths.has(file.path) && file.path === nextPath)) {
      throw new Error(`An item already exists at ${nextPath}`);
    }
  }

  const now = new Date().toISOString();
  const moved = snapshot.files.map((file) => {
    const nextPath = moves.get(file.path);
    return nextPath ? { ...file, path: nextPath, modifiedAt: now } : file;
  });
  const files = withAnnotationsFile(
    moved,
    remapAnnotationsForMove(annotationsOf(snapshot), source, destination),
    now,
  );
  const commit = await createCommit(
    files,
    snapshot.commits.at(-1) ?? null,
    `Move ${source} to ${target || 'repository root'}`,
    author,
    now,
  );
  return {
    ...snapshot,
    files: canonicalFiles(files),
    commits: [...snapshot.commits, commit],
    head: commit.id,
    version: snapshot.version + 1,
  };
};

/** Local working-copy upsert without creating a signed commit (queued save mode). */
export const draftUpsertFile = (
  snapshot: RepositorySnapshot,
  path: string,
  content: string,
  mimeType: string,
): RepositorySnapshot => {
  assertMutableUserPath(path, 'write');
  const now = new Date().toISOString();
  const nextFile: RepositoryFile = {
    path,
    content,
    mimeType,
    size: encoder.encode(content).length,
    modifiedAt: now,
  };
  return {
    ...snapshot,
    files: canonicalFiles([...snapshot.files.filter((file) => file.path !== path), nextFile]),
  };
};

export const draftUpsertFiles = (
  snapshot: RepositorySnapshot,
  updates: Array<{ path: string; content: string; mimeType: string }>,
): RepositorySnapshot => {
  if (!updates.length) return snapshot;
  for (const update of updates) assertMutableUserPath(update.path, 'write');
  const paths = new Set(updates.map((update) => update.path));
  if (paths.size !== updates.length) throw new Error('Dropped files must have unique names');
  const now = new Date().toISOString();
  const nextFiles: RepositoryFile[] = updates.map((update) => ({
    path: update.path,
    content: update.content,
    mimeType: update.mimeType,
    size: encoder.encode(update.content).length,
    modifiedAt: now,
  }));
  return {
    ...snapshot,
    files: canonicalFiles([
      ...snapshot.files.filter((file) => !paths.has(file.path)),
      ...nextFiles,
    ]),
  };
};

export const draftRemovePath = (snapshot: RepositorySnapshot, path: string): RepositorySnapshot => {
  const source = path.replace(/^\/+|\/+$/g, '');
  if (!source) throw new Error('Choose a file or folder to delete');
  assertMutableUserPath(source, 'delete');
  const remaining = snapshot.files.filter(
    (file) => file.path !== source && !file.path.startsWith(`${source}/`),
  );
  if (remaining.length === snapshot.files.length)
    throw new Error('The source path no longer exists');
  return {
    ...snapshot,
    files: canonicalFiles(
      withAnnotationsFile(remaining, dropAnnotationsForPath(annotationsOf(snapshot), source)),
    ),
  };
};

const draftRelocatePath = (
  snapshot: RepositorySnapshot,
  sourcePath: string,
  destinationPath: string,
  action: 'move' | 'rename',
): RepositorySnapshot => {
  const source = sourcePath.replace(/^\/+|\/+$/g, '');
  const destination = destinationPath.replace(/^\/+|\/+$/g, '');
  if (!source) throw new Error(`Choose a file or folder to ${action}`);
  if (!destination) throw new Error(`Choose a destination to ${action}`);
  assertMutableUserPath(source, action);
  assertMutableUserPath(destination, action);
  if (destination === source) {
    throw new Error(
      action === 'rename'
        ? 'That is already the current name'
        : 'This item is already in that folder',
    );
  }
  if (destination.startsWith(`${source}/`)) {
    throw new Error('A folder cannot be moved into itself');
  }

  const affected = snapshot.files.filter(
    (file) =>
      !isHiddenMetaPath(file.path) && (file.path === source || file.path.startsWith(`${source}/`)),
  );
  if (!affected.length) throw new Error('The source path no longer exists');

  const affectedPaths = new Set(affected.map((file) => file.path));
  const moves = new Map(
    affected.map((file) => [file.path, `${destination}${file.path.slice(source.length)}`]),
  );
  for (const nextPath of moves.values()) {
    if (snapshot.files.some((file) => !affectedPaths.has(file.path) && file.path === nextPath)) {
      throw new Error(`An item already exists at ${nextPath}`);
    }
  }

  const now = new Date().toISOString();
  const moved = snapshot.files.map((file) => {
    const nextPath = moves.get(file.path);
    return nextPath ? { ...file, path: nextPath, modifiedAt: now } : file;
  });
  return {
    ...snapshot,
    files: canonicalFiles(
      withAnnotationsFile(
        moved,
        remapAnnotationsForMove(annotationsOf(snapshot), source, destination),
        now,
      ),
    ),
  };
};

export const draftMovePath = (
  snapshot: RepositorySnapshot,
  sourcePath: string,
  targetFolder: string,
): RepositorySnapshot => {
  const source = sourcePath.replace(/^\/+|\/+$/g, '');
  const target = targetFolder.replace(/^\/+|\/+$/g, '');
  const name = source.split('/').at(-1)!;
  const destination = target ? `${target}/${name}` : name;
  return draftRelocatePath(snapshot, source, destination, 'move');
};

export const draftRenamePath = (
  snapshot: RepositorySnapshot,
  sourcePath: string,
  nextName: string,
): RepositorySnapshot => {
  const source = sourcePath.replace(/^\/+|\/+$/g, '');
  const name = nextName.trim().replace(/^\/+|\/+$/g, '');
  if (!name || name.includes('/')) {
    throw new Error('Enter a single path segment as the new name');
  }
  const parent = source.includes('/') ? source.slice(0, source.lastIndexOf('/')) : '';
  const destination = parent ? `${parent}/${name}` : name;
  return draftRelocatePath(snapshot, source, destination, 'rename');
};

export const draftSetAnnotation = (
  snapshot: RepositorySnapshot,
  path: string,
  annotation: EntryAnnotation | null,
): RepositorySnapshot => {
  assertMutableUserPath(path, 'annotate');
  const now = new Date().toISOString();
  return {
    ...snapshot,
    files: canonicalFiles(
      withAnnotationsFile(
        snapshot.files,
        withAnnotation(annotationsOf(snapshot), path, annotation),
        now,
      ),
    ),
  };
};

/** Turn the current working tree into one signed commit (used by immediate + queued flush). */
export const commitWorkingSnapshot = async (
  snapshot: RepositorySnapshot,
  message: string,
  author: string,
): Promise<RepositorySnapshot> => {
  const now = new Date().toISOString();
  const commit = await createCommit(
    snapshot.files,
    snapshot.commits.at(-1) ?? null,
    message,
    author,
    now,
  );
  return {
    ...snapshot,
    files: canonicalFiles(snapshot.files),
    commits: [...snapshot.commits, commit],
    head: commit.id,
    version: snapshot.version + 1,
  };
};

export const updateAnnotation = async (
  snapshot: RepositorySnapshot,
  path: string,
  annotation: EntryAnnotation | null,
  author: string,
): Promise<RepositorySnapshot> => {
  const drafted = draftSetAnnotation(snapshot, path, annotation);
  return commitWorkingSnapshot(
    drafted,
    annotation ? `Annotate ${path}` : `Clear annotations on ${path}`,
    author,
  );
};

export interface DiffLine {
  type: 'same' | 'add' | 'remove';
  value: string;
  oldLine?: number;
  newLine?: number;
}

export const lineDiff = (before: string, after: string): DiffLine[] => {
  if (before === after) return [];
  const left = before.split('\n');
  const right = after.split('\n');
  const rows = left.length + 1;
  const columns = right.length + 1;
  const matrix = Array.from({ length: rows }, () => Array<number>(columns).fill(0));
  for (let i = left.length - 1; i >= 0; i -= 1) {
    for (let j = right.length - 1; j >= 0; j -= 1) {
      matrix[i]![j] =
        left[i] === right[j]
          ? 1 + matrix[i + 1]![j + 1]!
          : Math.max(matrix[i + 1]![j]!, matrix[i]![j + 1]!);
    }
  }
  const result: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < left.length || j < right.length) {
    if (i < left.length && j < right.length && left[i] === right[j]) {
      result.push({ type: 'same', value: left[i]!, oldLine: i + 1, newLine: j + 1 });
      i += 1;
      j += 1;
    } else if (
      j < right.length &&
      (i === left.length || matrix[i]![j + 1]! >= matrix[i + 1]![j]!)
    ) {
      result.push({ type: 'add', value: right[j]!, newLine: j + 1 });
      j += 1;
    } else {
      result.push({ type: 'remove', value: left[i]!, oldLine: i + 1 });
      i += 1;
    }
  }
  return result;
};

export const topLevelFolders = (files: RepositoryFile[]): string[] =>
  [
    ...new Set(
      files
        .filter((file) => !isHiddenMetaPath(file.path))
        .map((file) => file.path.split('/')[0])
        .filter((folder): folder is string => Boolean(folder)),
    ),
  ].sort();

export const filesWithin = (files: RepositoryFile[], folder: string): RepositoryFile[] =>
  files.filter(
    (file) =>
      !isHiddenMetaPath(file.path) && (file.path === folder || file.path.startsWith(`${folder}/`)),
  );
